'use client';

import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import { FeatureRequests } from './FeatureRequests';
import { ProductUpdates, type ProductUpdateItem } from './ProductUpdates';
import type { UpdateChangeHandler, UpdateToggleHandler } from './UpdateToggle';
import type { FeatureRequestsApi } from '../lib/requests-shared';

export interface ProductDrawerProps {
  open: boolean;
  onClose: () => void;
  requestsApi: FeatureRequestsApi;
  updates: ProductUpdateItem[];
  onToggleUpdate: UpdateToggleHandler;
  /** After an update toggle saved: (updateKey, the saved value). */
  onUpdateChange?: UpdateChangeHandler;
  initialTab?: 'updates' | 'requests';
  productLabel?: string;
}

export function ProductDrawer({ open, onClose, requestsApi, updates, onToggleUpdate, onUpdateChange, initialTab = 'updates', productLabel = 'Your product' }: ProductDrawerProps) {
  const uid = useId();
  const [tab, setTab] = useState(initialTab);
  const [startInComposer, setStartInComposer] = useState(false);
  const [canSubmit, setCanSubmit] = useState(true);
  const panel = useRef<HTMLElement>(null);
  const close = useRef<HTMLButtonElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return;
    const previouslyFocused = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    close.current?.focus();
    function focusable(): HTMLElement[] {
      return Array.from(panel.current?.querySelectorAll<HTMLElement>('button:not(:disabled), a[href], input:not(:disabled), textarea:not(:disabled), select:not(:disabled), [tabindex="0"]') ?? []).filter((element) => !element.closest('[hidden]') && element.getClientRects().length > 0);
    }
    function onKeyDown(event: globalThis.KeyboardEvent) {
      if (event.key === 'Escape') { event.preventDefault(); onCloseRef.current(); }
      if (event.key !== 'Tab') return;
      const items = focusable();
      const first = items[0]; const last = items.at(-1);
      if (!first || !last) { event.preventDefault(); panel.current?.focus(); return; }
      if (event.shiftKey && (document.activeElement === first || !panel.current?.contains(document.activeElement))) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && (document.activeElement === last || !panel.current?.contains(document.activeElement))) { event.preventDefault(); first.focus(); }
    }
    function onFocus(event: FocusEvent) {
      if (event.target instanceof Node && !panel.current?.contains(event.target)) close.current?.focus();
    }
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('focusin', onFocus);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('focusin', onFocus);
      if (previouslyFocused instanceof HTMLElement && previouslyFocused.isConnected) previouslyFocused.focus();
    };
  }, [open]);

  function chooseTab(next: 'updates' | 'requests') { setTab(next); setStartInComposer(false); }
  function tabKey(event: KeyboardEvent<HTMLButtonElement>) {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    const next = event.key === 'Home' ? 'updates' : event.key === 'End' ? 'requests' : tab === 'updates' ? 'requests' : 'updates';
    chooseTab(next);
    document.getElementById(`${uid}-${next}-tab`)?.focus();
  }

  return <div className="bay-pd-overlay" hidden={!open} onClick={(event) => { if (event.target === event.currentTarget) onClose(); }}>
    <aside className="bay-pd" role="dialog" aria-modal="true" aria-labelledby={`${uid}-title`} aria-description={`${productLabel} product information`} ref={panel} tabIndex={-1}>
      <header className="bay-pd-header"><h1 id={`${uid}-title`}>{tab === 'updates' ? 'Product Updates' : 'Feature Requests'}</h1><button type="button" ref={close} onClick={onClose} className="bay-fr-icon-button" aria-label="Close product drawer"><svg width="28" height="28" viewBox="0 0 28 28" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true"><path d="m5 5 18 18M23 5 5 23" /></svg></button></header>
      <div className="bay-pd-tabs" role="tablist" aria-label="Product information">
        <button type="button" role="tab" id={`${uid}-updates-tab`} aria-selected={tab === 'updates'} aria-controls={`${uid}-updates-panel`} tabIndex={tab === 'updates' ? 0 : -1} onKeyDown={tabKey} onClick={() => chooseTab('updates')}>Product Updates</button>
        <button type="button" role="tab" id={`${uid}-requests-tab`} aria-selected={tab === 'requests'} aria-controls={`${uid}-requests-panel`} tabIndex={tab === 'requests' ? 0 : -1} onKeyDown={tabKey} onClick={() => chooseTab('requests')}>Feature Requests</button>
      </div>
      <div className="bay-pd-content bay-pd-content-updates" role="tabpanel" id={`${uid}-updates-panel`} aria-labelledby={`${uid}-updates-tab`} hidden={tab !== 'updates'} tabIndex={0}>
        <ProductUpdates updates={updates} onToggle={onToggleUpdate} onChange={onUpdateChange} showHeader={false} onSuggest={canSubmit ? () => { setStartInComposer(true); setTab('requests'); } : undefined} />
      </div>
      <div className="bay-pd-content" role="tabpanel" id={`${uid}-requests-panel`} aria-labelledby={`${uid}-requests-tab`} hidden={tab !== 'requests'} tabIndex={0}>
        <FeatureRequests api={requestsApi} startInComposer={startInComposer} onSubmissionAccessChange={setCanSubmit} />
      </div>
    </aside>
  </div>;
}
